import { EmergencyService } from '@app/core/auth/component/emergency-login/emergency-login.service';
import { NgModule, Optional, SkipSelf, NO_ERRORS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule, HttpClient } from '@angular/common/http';
import { storeFreeze } from 'ngrx-store-freeze';
import { debug } from './meta-reducers/debug.reducer';
import { APP_CONFIG, AppConfig } from '@app/config/app.config';
import { authReducer } from './auth/auth.reducer';

import { AuthEffects } from './auth/auth.effects';
import { EffectsModule } from '@ngrx/effects';

import { AuthenticationService } from '@app/core/auth/authentication.service';
import { ProgressBarService } from '@app/shared/services/progress-bar.service';
import { AuthGuardService } from './auth/auth-guard.service';
import { LoginGuardService } from './auth/login-guard.service';

import { ProgressInterceptor } from '@app/shared/interceptors/progress.interceptor';
import { TimingInterceptor } from '@app/shared/interceptors/timing.interceptor';

import { InfoModule } from './info';
import { MaterialModule } from '@app/shared/modules/material.module';
import { AuthModule } from '@app/core/auth/auth.module';
import { SharedModule } from '@app/shared/modules/shared.module';

import { NavModule } from '@app/core/nav/nav.module';
import { LanguageReducer } from '@app/core/nav/lang-switcher/lang.reducer';
import { LangEffects } from '@app/core/nav/lang-switcher/lang.effects';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { HttpLoaderFactory } from '@app/core/app.translate.factory';
import { FooterComponent } from '@app/core/footer/footer.component';
import { InfoService } from '@app/core/info/info.service';
import { MatDialogModule } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';

// used to create fake backend
import { fakeBackendProvider } from '@app/shared/interceptors/fake-beckend.interceptor';
import { HttpErrorHandler } from '@app/shared/services/http-error-handler.service';
import { MessageService } from '@app/shared/services/message.service';
import { ComponentTitleService } from '@app/shared/services/component-title.service';
import { ManageService } from '@app/certificates/manage/manage.service';
import { DevicesService } from '@app/certificates/devices/devices.service';
import { SettingsService } from '@app/profile/settings/settings.service';
import { settingsReducer } from '@app/profile/settings/settings.reducer';
import { SettingsEffects } from '@app/profile/settings/settings.effects';
import { manageDeviceReducer } from '@app/certificates/devices/devices.reducer';
import { packsReducer } from '@app/certificates/packs/packs.reducer';
import { historyReducer } from '@app/certificates/history/history.reducer';
import { profilePlanReducer } from '@app/profile/profile-remaining-count/profile-plan.reducer';
import { ProfilePlanEffects } from '@app/profile/profile-remaining-count/profile-plan.effects';
import { manageCertificateReducer } from '@app/certificates/manage/manage.reducer';
import { localStorageSync } from 'ngrx-store-localstorage';
import { StoreModule, ActionReducer, MetaReducer } from '@ngrx/store';
import { environment } from '@env/environment.dev';
import { ManageCertificateEffects } from '@app/certificates/manage/manage.effects';
import { ManageDevicesEffects } from '@app/certificates/devices/devices.effects';
import { PackEffects } from '@app/certificates/packs/packs.effects';
import { HistoryService } from '@app/certificates/history/history.service';
import { NomenclatureService } from './nomenclature/nomenclature.service';

import { CoreRoutingModule } from './core-routing.module';
import { LoggerService } from '@app/shared/services/logger.service';
import { ConsentsService } from '@app/certificates/consents/consents.service';
import { nomenclatureReducer } from './nomenclature/nomenclature.reducer';
import { BusinessService } from '@app/business/business.service';

export function localStorageSyncReducer(reducer: ActionReducer<any>): ActionReducer<any> {
  return localStorageSync(
    {keys: ['auth', 'certificates', 'devices', 'history', 'language', 'packs', 'settings', 'profilePlan', 'nomenclature'],
    rehydrate: true,
    storage: sessionStorage})(reducer);
}
const metaReducers: Array<MetaReducer<any, any>> = [localStorageSyncReducer];

if (!environment.production) {
  metaReducers.unshift(debug, storeFreeze);
}

@NgModule({
  imports: [
    // angular
    CommonModule,
    HttpClientModule,

    // other
    CoreRoutingModule,
    SharedModule,
    MaterialModule,
    InfoModule,
    AuthModule,
    NavModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),

    // ngrx
    StoreModule.forRoot(
      {
        auth: authReducer,
        settings: settingsReducer,
        language: LanguageReducer,
        certificates: manageCertificateReducer,
        devices: manageDeviceReducer,
        history: historyReducer,
        packs: packsReducer,
        profilePlan: profilePlanReducer,
        nomenclature: nomenclatureReducer
      },
      { metaReducers }
    ),
    EffectsModule.forRoot([
      AuthEffects,
      SettingsEffects,
      LangEffects,
      ManageCertificateEffects,
      ManageDevicesEffects,
      PackEffects,
      ProfilePlanEffects
    ])
  ],
  exports: [
    CommonModule,
    HttpClientModule,
    MaterialModule,
    InfoModule,
    // AuthModule,
    NavModule,
    SharedModule,
    FooterComponent,
    TranslateModule
  ],
  declarations: [FooterComponent],
  schemas: [NO_ERRORS_SCHEMA],
  providers: [
    HttpClient,
    ProgressBarService,
    AuthenticationService,
    HttpErrorHandler,
    MessageService,
    AuthGuardService,
    LoginGuardService,
    { provide: APP_CONFIG, useValue: AppConfig },
    { provide: HTTP_INTERCEPTORS, useClass: ProgressInterceptor, multi: true, deps: [ProgressBarService, InfoService, MatDialogModule] },
    { provide: HTTP_INTERCEPTORS, useClass: TimingInterceptor, multi: true },
    ManageService,
    DevicesService,
    BusinessService,
    ConsentsService,
    ComponentTitleService,
    fakeBackendProvider,
    NomenclatureService,
    SettingsService,
    HistoryService,
    MatInputModule,
    EmergencyService,
    LoggerService
  ]
})
export class CoreModule {
  constructor(
    @Optional()
    @SkipSelf()
    parentModule: CoreModule
  ) {
    if (parentModule) {
      throw new Error('CoreModule is already loaded. Import only in AppModule');
    }
  }
}
